import React, { useState, createContext, useEffect, useContext } from "react";
import { ECLLoader } from "../../eclComponents/";
import { stringSort } from "../sorters/sort";
import getIndexedDB from "../indexedDB/getIndexedDB";
import serviceQuery from "../requests/ServiceQuery";
import { AuthContext } from "./AuthContext";
import { translate } from "../Translate";
import { ToastContext } from "./ToastContext";
const IDBWorker = getIndexedDB();

/**
 * User Context holds all meta information related to users available on the server
 * @type {Object}
 * @property {array} all - returns a list of all users
 * @property {array} activeUsers - returns a list of all active users
 * @property {object} currentUser - returns the user info for the client's user
 * @property {array} metadataKeys - returns a list of all metadata keys on server
 * @property {array} allRoles - returns a list of all roles on server
 * @property {function} refresh - () - updates the user context state by requesting all information
 * @property {function} get - (userUUID) - returns a user entry for the given UUID
 * @property {function} getList - ([userUUIDs]) - returns a list of user entries for the given UUIDs
 * */
export const UserContext = createContext();

//Only props provided are props.children, which are nested inside the context jsx element
export function UserContextProvider(props) {
   const [users, setUsers] = useState(null);
   const [error, setError] = useState(false);

   const auth = useContext(AuthContext);
   const addToast = useContext(ToastContext);

   //Fetchusers populates the users state, as well as refreshes it.
   const fetchUsers = async () => {
      //****USER INFO AREA *****//
      //Provides metadata, roles and user info for each user
      const metaDataBatchResponse = await serviceQuery({
         message: "UserRoleMetadataBatch",
         query: {},
      });

      if (!metaDataBatchResponse?.Payload?.usersRoleMetadata) {
         throw new Error("Error fetching user metadata");
      }

      //Provides userInfo (But used only for SCIM)
      const authUserListResponse = await serviceQuery({
         message: "AuthUserList",
         query: {},
      });

      if (!authUserListResponse?.Payload?.users) {
         throw new Error("Error fetching user list");
      }

      //provides all active metadata keys
      const metaDataKeysResponse = await serviceQuery({
         message: "UserGetMetadataKeysMessage",
         query: {},
      });

      if (!metaDataKeysResponse?.Payload?.keys) {
         throw new Error("Error fetching metadata");
      }

      //provides all active roles (and SCIM status)
      const authRoleList = await serviceQuery({
         message: "AuthRoleListAll",
         query: {},
      });

      if (!authRoleList?.Payload?.roles) {
         throw new Error("Error fetching roles");
      }

      let userData = metaDataBatchResponse.Payload.usersRoleMetadata;
      authUserListResponse.Payload.isSCIMControlled.forEach((boolVal, i) => {
         userData[i].SCIM = boolVal;
      });

      //Keep role information with user context, since only users can have roles
      const allRoles = authRoleList.Payload.roles
         .map((r, i) => {
            return {
               role: r,
               SCIM: authRoleList.Payload.isSCIMControlled[i],
            };
         })
         .sort((a, b) => {
            return stringSort(a.role, b.role);
         });

      // use returned metadata keys to filter out/add proper metadata for each user
      const userMetaKeys = [...metaDataKeysResponse.Payload.keys].sort(
         stringSort,
      );

      //use provided meta keys to clean out unused metadata
      const userList = userData
         .map((user) => {
            let treatedMeta = {};
            //Use keys defined in metadata keys to map active user metadata values
            userMetaKeys.forEach((metaKey) => {
               const metaKeyVal = user.metadata.find(
                  (meta) => meta.key === metaKey,
               );
               if (metaKeyVal) {
                  treatedMeta[metaKey] = metaKeyVal.value;
               } else {
                  treatedMeta[metaKey] = "";
               }
            });
            user.metadata = treatedMeta;
            //Filter out any possible bad roles
            user.roles = user.roles.filter((role) =>
               allRoles.find((r) => r.role === role),
            );
            return user;
         })
         .sort(stringSort);

      //***** USER AVATAR INFO *****/
      //get default avatars, send them to IDB (would be nice to incorporate actual caching at some point)
      const defaultAvatarsResponse = await serviceQuery({
         message: "AvatarListMessage",
         query: {},
      });

      await IDBWorker.putAll(
         "defaultAvatars",
         defaultAvatarsResponse.Payload.avatars,
      ).catch((error) => {
         console.error(error);
      });

      //get custom avatars, send them to IDB
      const customAvatarsResponse = await serviceQuery({
         message: "UserAvatarCustomBatchMessage",
         query: {},
      });

      const customAvatars = [];

      Object.entries(customAvatarsResponse.Payload.allCustomAvatarData).forEach(
         ([k, v]) => {
            customAvatars.push({ uuid: k, data: v });
         },
      );
      await IDBWorker.putAll("customAvatars", customAvatars).catch((error) => {
         console.error(error);
      });

      //create indexes so that we can hold users as an array, but retrieve user info without running a array.find every time
      let indexIDs = {};
      userList.forEach((user, i) => (indexIDs[user.id.toLowerCase()] = i));

      // Pass everything we want to pass in this context down through users
      setUsers({
         all: userList,
         activeUsers: userList.filter((user) => !user.deactivated),
         currentUser: userList.find((u) => u.id === auth.credentials.userID),
         metadataKeys: userMetaKeys,
         allRoles: allRoles,
         refresh: fetchUsers,
         //Use indexes to get userinfo fast, returns null if not found, else returns user object
         get: (id) => {
            if (typeof id !== "string") {
               return null;
            }
            const treated = id.toLowerCase();
            if (Object.keys(indexIDs).includes(treated)) {
               if (userList[indexIDs[treated]]) {
                  return userList[indexIDs[treated]];
               }
            }
            console.error(`ID: ${id} not in context`);
            return null;
         },
         //getList returns a list of user information, which unknown ids returned as null
         getList: (ids) => {
            if (!Array.isArray(ids)) {
               console.error("No list provided to the get List function");
               return null;
            }
            return ids.map((id) => {
               if (typeof id !== "string") {
                  return null;
               }
               const treated = id.toLowerCase();
               if (Object.keys(indexIDs).includes(treated)) {
                  if (userList[indexIDs[treated]]) {
                     return userList[indexIDs[treated]];
                  }
               }
               console.error(`ID: ${id} not in context (list)`);
               return null;
            });
         },
      });
   };

   useEffect(() => {
      fetchUsers().catch((error) => {
         console.error(error);
         setError(translate("Error fetching user data"));
         addToast(translate("Error fetching user data"));
         auth.logout();
      });
   }, []);

   if (error) {
      return <ECLLoader message={error} />;
   }

   return (
      <UserContext.Provider value={users}>
         {props.children}
      </UserContext.Provider>
   );
}
